import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import Topbar from "@/components/Topbar";
import { apiClient, formatDate, POLE_LABELS, API, TICKET_STATUS_LABELS, TICKET_STATUS_META, TICKET_PRIORITY_LABELS, TICKET_CATEGORY_LABELS } from "@/lib/api";
import { usePoles } from "@/context/PolesContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import AttachmentPicker from "@/components/AttachmentPicker";
import { useAuth } from "@/context/AuthContext";
import { ArrowLeft, ChatCircle, LockKey, PaperPlaneTilt, FilePdf, Image as ImageIcon, Clock, UserCircle, ArrowClockwise, Archive, WarningCircle } from "@phosphor-icons/react";
import { toast } from "sonner";

const STATUSES = Object.keys(TICKET_STATUS_LABELS);
const PRIORITIES = Object.keys(TICKET_PRIORITY_LABELS);

export default function TicketDetail() {
  const { poles, poleLabel } = usePoles();
  const { id } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();

  const [ticket, setTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [history, setHistory] = useState([]);
  const [staffList, setStaffList] = useState([]);
  const [loading, setLoading] = useState(true);

  // Reply form
  const [reply, setReply] = useState("");
  const [replyInternal, setReplyInternal] = useState(false);
  const [replyStatus, setReplyStatus] = useState("in_progress");
  const [replyFiles, setReplyFiles] = useState([]);
  const [posting, setPosting] = useState(false);

  const openAttachment = useCallback(async (attId, filename) => {
    const win = window.open("about:blank", "_blank");
    try {
      const res = await apiClient.get(`/tickets/${id}/attachments/${attId}/file`, { responseType: "blob" });
      const url = URL.createObjectURL(res.data);
      if (win) {
        win.location.href = url;
      } else {
        const link = document.createElement("a");
        link.href = url;
        link.download = filename || "document";
        link.click();
      }
      setTimeout(() => URL.revokeObjectURL(url), 60000);
    } catch (err) {
      if (win) win.close();
      console.warn("openAttachment failed", err);
      toast.error("Impossible d'ouvrir la pièce jointe");
    }
  }, [id]);

  const fetchDetail = useCallback(async () => {
    setLoading(true);
    try {
      const { data } = await apiClient.get(`/tickets/${id}`);
      setTicket(data.ticket);
      setMessages(data.messages);
      setHistory(data.history);
    } catch (e) {
      toast.error("Ticket introuvable");
      navigate("/tickets");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  const fetchStaff = useCallback(async () => {
    if (user?.role !== "admin" && user?.role !== "responsable") return;
    try {
      const { data } = await apiClient.get("/auth/users");
      setStaffList(data.filter((u) => u.role !== "client"));
    } catch (err) {
      console.warn("fetchStaff failed", err);
    }
  }, [user]);

  useEffect(() => {
    fetchDetail();
    fetchStaff();
  }, [fetchDetail, fetchStaff]);

  const changeStatus = async (status) => {
    try {
      await apiClient.patch(`/tickets/${id}/status`, { status });
      toast.success("Statut mis à jour");
      fetchDetail();
    } catch (e) { toast.error(e.response?.data?.detail || "Échec"); }
  };

  const changePriority = async (priority) => {
    try {
      await apiClient.patch(`/tickets/${id}/priority`, { priority });
      toast.success("Priorité mise à jour");
      fetchDetail();
    } catch (e) { toast.error(e.response?.data?.detail || "Échec"); }
  };

  const changeService = async (service_id) => {
    try {
      await apiClient.patch(`/tickets/${id}/assign`, { service_id });
      toast.success("Service mis à jour");
      fetchDetail();
    } catch (e) { toast.error(e.response?.data?.detail || "Échec"); }
  };

  const changeAssignee = async (assignee_user_id) => {
    try {
      await apiClient.patch(`/tickets/${id}/assign`, { assignee_user_id: assignee_user_id === "none" ? null : assignee_user_id });
      toast.success("Responsable mis à jour");
      fetchDetail();
    } catch (e) { toast.error(e.response?.data?.detail || "Échec"); }
  };

  const closeTicket = async () => {
    if (!confirm("Clôturer ce ticket ?")) return;
    try { await apiClient.post(`/tickets/${id}/close`); toast.success("Clôturé"); fetchDetail(); }
    catch (e) { toast.error("Échec"); }
  };

  const reopenTicket = async () => {
    try { await apiClient.post(`/tickets/${id}/reopen`); toast.success("Réouvert"); fetchDetail(); }
    catch (e) { toast.error("Échec"); }
  };

  const archiveTicket = async () => {
    try { await apiClient.post(`/tickets/${id}/archive`); toast.success("Ticket archivé"); fetchDetail(); }
    catch (e) { toast.error(e.response?.data?.detail || "Échec"); }
  };

  const unarchiveTicket = async () => {
    try { await apiClient.post(`/tickets/${id}/unarchive`); toast.success("Ticket désarchivé"); fetchDetail(); }
    catch (e) { toast.error(e.response?.data?.detail || "Échec"); }
  };

  const postMessage = async (e) => {
    e.preventDefault();
    if (!reply.trim()) { toast.error("Message vide"); return; }
    setPosting(true);
    try {
      const fd = new FormData();
      fd.append("body", reply.trim());
      fd.append("is_internal", replyInternal ? "true" : "false");
      if (!replyInternal) fd.append("set_status", replyStatus);
      replyFiles.forEach((f) => fd.append("files", f));
      await apiClient.post(`/tickets/${id}/messages`, fd, { headers: { "Content-Type": "multipart/form-data" } });
      setReply(""); setReplyFiles([]); setReplyInternal(false);
      toast.success(replyInternal ? "Commentaire interne ajouté" : "Réponse publique envoyée");
      fetchDetail();
    } catch (err) { toast.error(err.response?.data?.detail || "Échec"); }
    finally { setPosting(false); }
  };

  if (loading || !ticket) {
    return (
      <>
        <Topbar title="Ticket" />
        <main className="px-4 sm:px-6 lg:px-8 py-8"><div className="text-sm text-zinc-500">Chargement...</div></main>
      </>
    );
  }

  const statusMeta = TICKET_STATUS_META[ticket.status];

  return (
    <>
      <Topbar
        title={`Ticket · ${ticket.reference}`}
        subtitle={ticket.subject}
        actions={
          <Button variant="outline" onClick={() => navigate("/tickets")} className="rounded-sm h-9 px-3 gap-2 border-zinc-300" data-testid="back-tickets">
            <ArrowLeft size={14} weight="bold" /> <span className="hidden sm:inline">Liste</span>
          </Button>
        }
      />

      <main className="px-4 sm:px-6 lg:px-8 py-5 sm:py-8 max-w-[1600px] w-full" data-testid="ticket-detail">
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
          {/* Left: conversation */}
          <div className="lg:col-span-2 space-y-4">
            {/* Original description */}
            <div className="bg-white border border-zinc-200 p-4 sm:p-5">
              <div className="flex items-center gap-2 mb-3">
                <UserCircle size={20} weight="duotone" className="text-zinc-500" />
                <div>
                  <div className="font-medium">{ticket.client_first_name} {ticket.client_last_name}</div>
                  <div className="text-[11px] text-zinc-500 font-mono">{formatDate(ticket.created_at)} · client</div>
                </div>
              </div>
              <div className="text-sm whitespace-pre-wrap break-words bg-zinc-50 border border-zinc-100 p-3 mb-2" data-testid="ticket-description">{ticket.description}</div>
              {ticket.transaction_number && (
                <div className="text-xs text-zinc-500">N° transaction : <span className="font-mono font-semibold text-zinc-800">{ticket.transaction_number}</span></div>
              )}
            </div>

            {/* Messages */}
            <div className="space-y-3">
              {messages.map((m) => (
                <div key={m.id} className={`border p-4 ${m.is_internal ? "border-yellow-200 bg-yellow-50" : "bg-white border-zinc-200"}`} data-testid={`message-${m.id}`}>
                  <div className="flex items-center justify-between gap-2 mb-2">
                    <div className="flex items-center gap-2">
                      {m.is_internal ? <LockKey size={16} weight="fill" className="text-yellow-700" /> : <ChatCircle size={16} weight="duotone" className="text-zinc-500" />}
                      <div>
                        <div className="text-sm font-medium">{m.author_name}</div>
                        <div className="text-[11px] text-zinc-500">{m.author_role} · {formatDate(m.created_at)}</div>
                      </div>
                    </div>
                    {m.is_internal && <span className="text-[10px] font-semibold uppercase tracking-wider text-yellow-800 bg-yellow-200 px-2 py-0.5">Interne</span>}
                  </div>
                  <div className="text-sm whitespace-pre-wrap break-words">{m.body}</div>
                  {m.attachments && m.attachments.length > 0 && (
                    <div className="flex flex-wrap gap-1 mt-2">
                      {m.attachments.map((a) => (
                        <button key={a.id} type="button" onClick={() => openAttachment(a.id, a.original_filename)} className="inline-flex items-center gap-1 px-1.5 py-0.5 bg-white border border-zinc-200 hover:border-zinc-900 text-[11px] cursor-pointer" data-testid={`ticket-att-${a.id}`}>
                          {a.kind === "image" ? <ImageIcon size={11} weight="duotone" className="text-blue-600" /> : <FilePdf size={11} weight="duotone" className="text-red-600" />}
                          <span className="max-w-[160px] truncate">{a.original_filename}</span>
                        </button>
                      ))}
                    </div>
                  )}
                </div>
              ))}
              {messages.length === 0 && (
                <div className="text-xs text-zinc-500 italic px-2">Aucun message pour le moment.</div>
              )}
            </div>

            {/* Reply form */}
            <form onSubmit={postMessage} className="bg-white border border-zinc-200 p-4 sm:p-5" data-testid="reply-form">
              <div className="flex items-center gap-2 mb-3">
                <button type="button" onClick={() => setReplyInternal(false)} className={`px-3 py-1.5 text-xs font-medium border ${!replyInternal ? "bg-zinc-900 text-white border-zinc-900" : "bg-white border-zinc-300"}`} data-testid="tab-public">
                  Réponse publique
                </button>
                <button type="button" onClick={() => setReplyInternal(true)} className={`px-3 py-1.5 text-xs font-medium border ${replyInternal ? "bg-yellow-500 text-white border-yellow-500" : "bg-white border-zinc-300"}`} data-testid="tab-internal">
                  <span className="inline-flex items-center gap-1"><LockKey size={11} weight="fill" /> Commentaire interne</span>
                </button>
              </div>
              <Textarea
                value={reply}
                onChange={(e) => setReply(e.target.value)}
                placeholder={replyInternal ? "Commentaire visible uniquement en interne..." : "Réponse visible par le client..."}
                className="rounded-sm border-zinc-300 min-h-[100px] mb-3"
                data-testid="reply-input"
              />
              <AttachmentPicker files={replyFiles} setFiles={setReplyFiles} compact testIdPrefix="reply" />
              <div className="flex flex-wrap items-center justify-end gap-2 mt-3">
                {!replyInternal && (
                  <div className="flex items-center gap-1.5 mr-auto">
                    <span className="label-tiny text-zinc-500">Statut après envoi</span>
                    <Select value={replyStatus} onValueChange={setReplyStatus}>
                      <SelectTrigger className="h-9 w-[170px] rounded-sm border-zinc-300 text-xs" data-testid="reply-status-select"><SelectValue /></SelectTrigger>
                      <SelectContent>
                        <SelectItem value="in_progress">En cours</SelectItem>
                        <SelectItem value="waiting_client">En attente du client</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                )}
                <Button type="submit" disabled={posting} className="bg-zinc-900 hover:bg-zinc-800 text-white rounded-sm h-9 px-4 gap-2" data-testid="post-message-btn">
                  <PaperPlaneTilt size={14} weight="bold" />
                  {posting ? "Envoi..." : (replyInternal ? "Ajouter le commentaire" : "Envoyer la réponse")}
                </Button>
              </div>
            </form>
          </div>

          {/* Right: metadata & actions */}
          <div className="space-y-4">
            <div className="bg-white border border-zinc-200 p-4">
              <div className="label-tiny text-zinc-500 mb-1">Statut</div>
              <div className={`inline-flex items-center gap-1.5 px-2.5 py-1 text-sm ${statusMeta.bg} ${statusMeta.text} mb-3`} data-testid="ticket-status">
                <span className={`w-2 h-2 rounded-full ${statusMeta.dot}`} />
                {TICKET_STATUS_LABELS[ticket.status]}
              </div>
              {ticket.is_overdue && (
                <div className="inline-flex items-center gap-1.5 px-2.5 py-1 text-sm bg-red-600 text-white mb-3 ml-2" data-testid="overdue-badge" title="Sans réponse depuis plus de 72h — remonté automatiquement">
                  <WarningCircle size={14} weight="fill" /> En retard
                </div>
              )}
              <Select value={ticket.status} onValueChange={changeStatus}>
                <SelectTrigger className="h-9 rounded-sm border-zinc-300" data-testid="status-select"><SelectValue /></SelectTrigger>
                <SelectContent>
                  {STATUSES.map((s) => (<SelectItem key={s} value={s}>{TICKET_STATUS_LABELS[s]}</SelectItem>))}
                </SelectContent>
              </Select>
              <div className="grid grid-cols-2 gap-2 mt-2">
                {ticket.status !== "closed" && <Button variant="outline" size="sm" onClick={closeTicket} className="rounded-sm h-8 text-xs" data-testid="close-btn">Clôturer</Button>}
                {(ticket.status === "closed" || ticket.status === "resolved") && <Button variant="outline" size="sm" onClick={reopenTicket} className="rounded-sm h-8 text-xs gap-1" data-testid="reopen-btn"><ArrowClockwise size={12} weight="bold" /> Réouvrir</Button>}
                {ticket.status === "closed" && !ticket.is_archived && <Button variant="outline" size="sm" onClick={archiveTicket} className="rounded-sm h-8 text-xs gap-1 border-zinc-300" data-testid="archive-btn"><Archive size={12} weight="bold" /> Archiver</Button>}
                {ticket.is_archived && <Button variant="outline" size="sm" onClick={unarchiveTicket} className="rounded-sm h-8 text-xs gap-1 border-zinc-300" data-testid="unarchive-btn"><ArrowClockwise size={12} weight="bold" /> Désarchiver</Button>}
              </div>
              {ticket.is_archived && (
                <div className="mt-2 inline-flex items-center gap-1.5 px-2 py-1 text-[11px] bg-zinc-100 text-zinc-600 border border-zinc-200" data-testid="archived-badge">
                  <Archive size={12} weight="fill" /> Archivé — masqué de la liste active
                </div>
              )}
            </div>

            <div className="bg-white border border-zinc-200 p-4 space-y-3">
              <div>
                <div className="label-tiny text-zinc-500 mb-1">Priorité</div>
                <Select value={ticket.priority} onValueChange={changePriority}>
                  <SelectTrigger className="h-9 rounded-sm border-zinc-300" data-testid="priority-select"><SelectValue /></SelectTrigger>
                  <SelectContent>{PRIORITIES.map((p) => (<SelectItem key={p} value={p}>{TICKET_PRIORITY_LABELS[p]}</SelectItem>))}</SelectContent>
                </Select>
              </div>
              <div>
                <div className="label-tiny text-zinc-500 mb-1">Service</div>
                <Select value={ticket.service_id || "none"} onValueChange={(v) => changeService(v === "none" ? null : v)}>
                  <SelectTrigger className="h-9 rounded-sm border-zinc-300" data-testid="service-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="none">Aucun</SelectItem>
                    {poles.map((p) => (<SelectItem key={p.id} value={p.id}>{p.name}</SelectItem>))}
                  </SelectContent>
                </Select>
              </div>
              {(user?.role === "admin" || user?.role === "responsable") && (
                <div>
                  <div className="label-tiny text-zinc-500 mb-1">Responsable assigné</div>
                  <Select value={ticket.assignee_user_id || "none"} onValueChange={changeAssignee}>
                    <SelectTrigger className="h-9 rounded-sm border-zinc-300" data-testid="assignee-select"><SelectValue placeholder="Aucun" /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="none">Aucun</SelectItem>
                      {staffList
                        .filter((s) => !ticket.service_id || !s.service_id || s.service_id === ticket.service_id || s.role === "admin")
                        .map((s) => (
                          <SelectItem key={s.id} value={s.id}>{s.name} · {s.role}{s.service_id ? ` · ${poleLabel(s.service_id)}` : ""}</SelectItem>
                        ))}
                    </SelectContent>
                  </Select>
                </div>
              )}
            </div>

            <div className="bg-white border border-zinc-200 p-4">
              <div className="label-tiny text-zinc-500 mb-2">Client</div>
              <div className="text-sm font-medium">{ticket.client_first_name} {ticket.client_last_name}</div>
              <div className="text-xs text-zinc-600 mt-0.5">{ticket.client_email}</div>
              {ticket.client_phone && <div className="text-xs text-zinc-600">{ticket.client_phone}</div>}
              <div className="mt-3 pt-3 border-t border-zinc-100 space-y-1 text-xs">
                <div><span className="text-zinc-500">Référence :</span> <span className="font-mono font-semibold">{ticket.reference}</span></div>
                <div><span className="text-zinc-500">Catégorie :</span> {TICKET_CATEGORY_LABELS[ticket.category] || ticket.category}</div>
                {ticket.transaction_number && <div><span className="text-zinc-500">N° transaction :</span> <span className="font-mono">{ticket.transaction_number}</span></div>}
                <div><span className="text-zinc-500">Créé :</span> <span className="font-mono">{formatDate(ticket.created_at)}</span></div>
                <div><span className="text-zinc-500">Dernière activité :</span> <span className="font-mono">{formatDate(ticket.last_activity_at || ticket.created_at)}</span></div>
                {ticket.resolved_at && <div><span className="text-zinc-500">Résolu :</span> <span className="font-mono">{formatDate(ticket.resolved_at)}</span></div>}
                {ticket.closed_at && <div><span className="text-zinc-500">Clôturé :</span> <span className="font-mono">{formatDate(ticket.closed_at)}</span></div>}
              </div>
            </div>

            {/* History */}
            <div className="bg-white border border-zinc-200 p-4">
              <div className="label-tiny text-zinc-500 mb-2 flex items-center gap-1"><Clock size={11} weight="bold" /> Historique</div>
              <div className="space-y-2 max-h-72 overflow-y-auto" data-testid="ticket-history">
                {history.map((h) => (
                  <div key={h.id} className="text-xs border-l-2 border-zinc-200 pl-2.5 py-0.5">
                    <div className="text-zinc-700"><span className="font-medium">{h.actor_name}</span> · {h.action.replace("_", " ")}</div>
                    {h.from_value && h.to_value && <div className="text-zinc-500 font-mono text-[10px]">{h.from_value} → {h.to_value}</div>}
                    <div className="text-zinc-400 font-mono text-[10px]">{formatDate(h.at)}</div>
                  </div>
                ))}
                {history.length === 0 && <div className="text-xs text-zinc-400 italic">Aucun événement</div>}
              </div>
            </div>
          </div>
        </div>
      </main>
    </>
  );
}
